import { useState, useRef, useEffect } from 'react';
import { Box, Paper, Typography, TextField, IconButton, Chip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { sendChat } from '../api/ai';

const SUGGESTIONS = [
  'Show all critical vulnerabilities',
  'What is the ATO status?',
  'STIG compliance summary',
  'Which devices are offline?',
  'Vulnerability summary',
  'RMF control status',
  'Generate executive summary',
  'Top patched vulnerabilities',
];

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 1.5 }}>
      <Box sx={{
        maxWidth: '75%',
        padding: '10px 14px',
        borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        backgroundColor: isUser ? '#00bcd4' : '#111827',
        border: isUser ? 'none' : '1px solid #1e3a5f',
      }}>
        {msg.content.split('\n').map((line, i) => {
          const isBold = line.startsWith('**') && line.endsWith('**');
          return (
            <Typography key={i} sx={{
              color: isUser ? '#000000' : (isBold ? '#ffffff' : '#c0cfe0'),
              fontSize: 13,
              fontWeight: isBold ? 700 : 400,
              lineHeight: 1.6,
              fontFamily: line.startsWith('•') || line.startsWith('  └') ? 'monospace' : 'inherit',
            }}>
              {isBold ? line.slice(2, -2) : line || ' '}
            </Typography>
          );
        })}
        <Typography sx={{ color: isUser ? '#006070' : '#4a5a70', fontSize: 10, marginTop: 0.5, textAlign: isUser ? 'right' : 'left' }}>
          {msg.time}
        </Typography>
      </Box>
    </Box>
  );
}

export default function AiAssistant({ systemId }) {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: "Hello. I'm ASPYN's AI assistant. Ask me anything about this system's vulnerabilities, STIG compliance, ATO status, or asset inventory.",
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  }]);
  const [input, setInput]     = useState('');
  const [thinking, setThinking] = useState(false);
  const [history, setHistory]   = useState([]);
  const [error, setError]       = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || thinking) return;

    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { role: 'user', content: q, time }]);
    setHistory(prev => [q, ...prev.slice(0, 4)]);
    setInput('');
    setThinking(true);
    setError(null);

    try {
      const data = await sendChat(systemId, q, history);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } catch (err) {
      setError('Failed to get a response. Check your connection.');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I was unable to retrieve data. Please try again.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', height: 'calc(100vh - 64px)', padding: 3, display: 'flex', gap: 2, overflow: 'hidden' }}>

      {/* Left Panel */}
      <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', minHeight: 0 }}>
        <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px' }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.5 }}>Quick Queries</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {SUGGESTIONS.map(s => (
              <Box key={s} onClick={() => send(s)} sx={{
                padding: '8px 10px', borderRadius: 1, border: '1px solid #1e3a5f',
                cursor: 'pointer', '&:hover': { borderColor: '#00bcd4', backgroundColor: '#0d2137' },
              }}>
                <Typography sx={{ color: '#c0cfe0', fontSize: 12 }}>{s}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {history.length > 0 && (
          <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px' }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.25 }}>Recent</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {history.map((h, i) => (
                <Typography key={i} onClick={() => send(h)} sx={{
                  color: '#4a5a70', fontSize: 12, cursor: 'pointer', padding: '4px 6px', borderRadius: 1,
                  '&:hover': { color: '#00bcd4', backgroundColor: '#0d2137' },
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{h}</Typography>
              ))}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Chat Area */}
      <Paper sx={{ flex: 1, minHeight: 0, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <Box sx={{ padding: '14px 20px', borderBottom: '1px solid #1e3a5f', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50' }} />
          <Typography sx={{ color: '#ffffff', fontWeight: 700 }}>ASPYN AI Assistant</Typography>
          <Chip label="Live Data" size="small" sx={{ backgroundColor: '#0d2137', color: '#00bcd4', fontSize: 10, height: 18, marginLeft: 'auto' }} />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {messages.map((m, i) => <Message key={i} msg={m} />)}
          {thinking && (
            <Box sx={{ display: 'flex', gap: 0.5, padding: '10px 14px', width: 'fit-content' }}>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{
                  width: 7, height: 7, borderRadius: '50%', backgroundColor: '#1e3a5f',
                  animation: 'pulse 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  '@keyframes pulse': { '0%, 100%': { opacity: 0.3 }, '50%': { opacity: 1 } },
                }} />
              ))}
            </Box>
          )}
          {error && (
            <Typography sx={{ color: '#f44336', fontSize: 12, padding: '4px 8px' }}>{error}</Typography>
          )}
          <div ref={bottomRef} />
        </Box>

        <Box sx={{ padding: '14px 20px', borderTop: '1px solid #1e3a5f', display: 'flex', gap: 1 }}>
          <TextField
            fullWidth size="small"
            placeholder="Ask about vulnerabilities, compliance, ATO status..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a1525', color: '#ffffff',
                '& fieldset': { borderColor: '#1e3a5f' },
                '&:hover fieldset': { borderColor: '#00bcd4' },
                '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
              },
              '& input::placeholder': { color: '#4a5a70' },
            }}
          />
          <IconButton onClick={() => send()} disabled={!input.trim() || thinking} sx={{
            backgroundColor: '#00bcd4', color: '#000000', borderRadius: 1,
            '&:hover': { backgroundColor: '#00acc1' },
            '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' },
          }}>
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}
